import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, CheckCircle, Shield, Clock, Users, Award, Target } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { Testimonial } from '../components/ui/Testimonial';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionH2 = motion.h2;
const MotionP = motion.p;

export default function WhyChooseUs() {
  const benefits = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Transparency & Trust",
      description: "No hidden costs, no surprises. We believe in clear communication and trustworthy operations."
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: "Quality Assurance",
      description: "Rigorous standards for container handling and repair, ensuring your assets are in perfect condition."
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "24/7 Availability",
      description: "Our yards and services are operational round-the-clock, ensuring you never face delays."
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Expert Team",
      description: "Skilled professionals with years of industry experience, dedicated to delivering excellence."
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Modern Facilities",
      description: "State-of-the-art equipment and infrastructure designed for efficiency and safety."
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Strategic Locations",
      description: "Conveniently located near major ports, minimizing transport time and logistics costs."
    }
  ];

  const testimonials = [
    {
      text: "At Mega Marine Container Terminals, we do things differently by focusing on building relationships, not just transactions.",
      author: "Our Difference"
    },
    {
      text: "No hidden costs. No compromises. Just transparency, quality, and trust in every container we handle.",
      author: "Our Promise"
    },
    {
      text: "You're choosing a partner — not just a yard. We're committed to your success as much as our own.",
      author: "Our Commitment"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <div className="relative py-24 bg-primary">
        <div className="absolute inset-0 overflow-hidden">          <img
            src="https://images.unsplash.com/photo-1624005340764-ca0a56593b06?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Container yard"
            className="w-full h-full object-cover opacity-20"
            onError={(e) => {
              // Fallback to local Photo2 image first
              if (e.currentTarget.src.includes('unsplash')) {
                e.currentTarget.src = "/assets/images/Photo2.jpg";
                console.log("WhyChooseUs hero image fallback: trying Photo2");
              } else {
                e.currentTarget.src = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80";
                console.warn("WhyChooseUs hero image failed, using secondary fallback");
              }
            }}
            onLoad={() => {
              console.log("WhyChooseUs hero image loaded successfully");
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <MotionH1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8"
            >
              Why Choose Us
            </MotionH1>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-10 md:mb-12"
            >
              Discover what makes Mega Marine Container Terminals the trusted choice for container yard operations
            </MotionP>
          </div>
        </div>
      </div>      {/* Main Benefits */}
      <Section
        title="Our Advantages"
        subtitle="What makes Mega Marine Container Terminals stand out from the competition"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {benefits.map((benefit, index) => (
            <MotionDiv
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white dark:bg-navy-light rounded-lg p-8 shadow-sm border border-gray-100 dark:border-navy"
            >
              <div className="w-14 h-14 mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white leading-snug">{benefit.title}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{benefit.description}</p>
            </MotionDiv>
          ))}
        </div>
      </Section>

      {/* Our Approach */}
      <Section
        title="Our Approach"
        subtitle="How we deliver value to our clients every day"
        className="bg-gray-50 dark:bg-navy"
      >
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <MotionDiv
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-bold mb-8 text-primary leading-tight">Redefining Industry Standards</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              At Mega Marine Container Terminals, we're not content with following industry standards—we're
              committed to setting new ones. Our approach combines innovative thinking with time-tested 
              expertise, ensuring we deliver container yard operations that exceed expectations.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="mt-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 leading-snug">Customer-Centric Solutions</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    We listen carefully to your needs and customize our services accordingly, ensuring 
                    perfect alignment with your business requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 leading-snug">Proactive Communication</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    We don't wait for you to ask—we keep you informed at every step, ensuring complete 
                    visibility into our operations and your containers.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2 leading-snug">Continuous Improvement</h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    We regularly evaluate our processes and technologies, ensuring we remain at the cutting 
                    edge of container yard operations and services.
                  </p>
                </div>
              </div>
            </div>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-lg overflow-hidden shadow-lg"
          >            <img
              src="https://images.unsplash.com/photo-1586528116375-74a6c52e31e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="Container operations"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to local Photo2 image first
                if (e.currentTarget.src.includes('unsplash')) {
                  e.currentTarget.src = "/assets/images/Photo2.jpg";
                  console.log("WhyChooseUs section image fallback: trying Photo2");
                } else {
                  e.currentTarget.src = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";
                  console.warn("WhyChooseUs section image failed, using secondary fallback");
                }
              }}
              onLoad={() => {
                console.log("WhyChooseUs section image loaded successfully");
              }}
            />
          </MotionDiv>
        </div>
      </Section>

      {/* Testimonials */}
      <Section
        title="Our Difference"
        subtitle="What sets us apart in the container yard industry"
      >
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Testimonial
              key={index}
              text={testimonial.text}
              author={testimonial.author}
            />
          ))}
        </div>
      </Section>

      {/* Our Promise */}
      <Section className="bg-navy text-white">
        <div className="max-w-3xl mx-auto text-center">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Award className="h-16 w-16 mx-auto mb-8 text-accent" />
          </MotionDiv>
          <MotionH2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            The Mega Marine Promise
          </MotionH2>
          <MotionP
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl mb-8 leading-relaxed text-gray-200"
          >
            When you choose Mega Marine Container Terminals, you're not just selecting a service provider—you're 
            partnering with a team that treats your containers as their own. We promise transparency, quality, 
            and reliability in every interaction, backed by our commitment to setting new standards in the 
            container yard industry.
          </MotionP>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center bg-accent hover:bg-accent-dark text-white px-8 py-3 rounded-md font-medium transition-colors duration-300"
            >
              Experience The Difference
            </a>
          </MotionDiv>
        </div>
      </Section>
    </div>
  );
}


